import { useState } from 'react';
import { buildingService } from 'services/api';
import PropertyCombobox from 'components/PropertyCombobox';
import SearchableSelect from 'components/SearchableSelect';
import styles from './BuildingFormModal.module.css';

// Mismas opciones que el asistente de creación de propiedades (torres, casas,
// locales...) — solo sugieren el nombre, se puede editar después (ej. "Torre 3").
// Singular a propósito: es el nombre base de UN edificio, no de la categoría
// completa (si hay varias, el admin le agrega el número, ej. "Casa 1", "Casa 2").
const TIPOS_TORRE = ['Torre', 'Casa', 'Local', 'Oficina', 'Parqueadero', 'Bodega'];
const LETRAS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)); // A-Z

// Arma el nombre final a partir de las 3 piezas — sin dejar que el usuario
// escriba el nombre completo a mano, que es justo lo que permitía errores de
// tipeo y espacios extra (ej. "Torre  1" con doble espacio, que antes se
// guardaba tal cual y no coincidía con nada al importar Excel). El número es
// un input type="number" (el navegador ya rechaza letras/espacios ahí) y la
// letra es un select fijo A-Z — no queda ningún campo libre donde meter un
// espacio de más.
const buildNombreTorre = ({ tipo, numero, letra }) =>
  tipo && numero ? `${tipo} ${numero}${letra || ''}` : '';

// Mismo tope que valida el backend (buildingsController.js).
const MAX_NOMBRE_TORRE = 100;

// Con noValidate el navegador ya no bloquea el envío por su cuenta — se
// revisa acá lo mismo que antes garantizaban los `required` nativos
// (tipo/número, y property_id para super_admin), con el mismo mensaje en el
// alert-danger que ya usa el resto de la app en vez del tooltip nativo.
function validarCampos(nombreParts, form, isSuperAdmin) {
  if (!nombreParts.tipo) return 'El tipo es requerido';
  const num = parseInt(nombreParts.numero, 10);
  if (!nombreParts.numero || !Number.isInteger(num) || num < 1) return 'El número es requerido y debe ser un entero positivo';
  if (isSuperAdmin && !form.property_id) return 'La propiedad es requerida';
  if (form.numero_torre.length > MAX_NOMBRE_TORRE) {
    return `El nombre de la torre no puede tener más de ${MAX_NOMBRE_TORRE} caracteres`;
  }
  return null;
}

// Descompone un nombre ya guardado (ej. "Torre 1A") en sus 3 piezas, para
// precargar el formulario al editar una torre existente.
const parseNombreTorre = (nombre) => {
  const m = String(nombre || '').trim().match(/^(.*?)\s*(\d+)\s*([A-Za-z]?)$/);
  if (!m) return { tipo: '', numero: '', letra: '' };
  return { tipo: m[1], numero: m[2], letra: m[3].toUpperCase() };
};

export default function BuildingFormModal({ editing, properties, isSuperAdmin, defaultPropertyId, onClose, onSaved }) {
  const [form, setForm] = useState({
    numero_torre: editing?.numero_torre || '',
    property_id:  editing?.property_id ?? (defaultPropertyId || ''),
  });
  const [nombreParts, setNombreParts] = useState(
    editing ? parseNombreTorre(editing.numero_torre) : { tipo: '', numero: '', letra: '' }
  );
  const [modalError, setModalError] = useState('');

  // Actualiza una pieza del nombre (tipo/numero/letra) y recalcula el nombre
  // completo que de verdad se envía al backend en form.numero_torre.
  const setNombrePart = (key, value) => {
    setNombreParts(prev => {
      const next = { ...prev, [key]: value };
      setForm(f => ({ ...f, numero_torre: buildNombreTorre(next) }));
      return next;
    });
    setModalError('');
  };

  const save = async (e) => {
    e.preventDefault();
    const campoError = validarCampos(nombreParts, form, isSuperAdmin);
    if (campoError) return setModalError(campoError);
    try {
      if (editing) await buildingService.update(editing.id, form);
      else         await buildingService.create(form);
      onSaved(editing ? 'Torre actualizada' : 'Torre creada');
    } catch (err) {
      setModalError(err.response?.data?.message || 'Error al guardar');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{editing ? 'Editar' : 'Nueva'} torre</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={save} noValidate>
          <div className="modal-body">
            {modalError && (
              <div className={`alert alert-danger ${styles.alertaModal}`}>
                <i className={`fas fa-exclamation-circle ${styles.iconoError}`} />
                {modalError}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Tipo *</label>
              <SearchableSelect value={nombreParts.tipo} onChange={(v) => setNombrePart('tipo', v)} placeholder="Seleccione..." minWidth={0} maxWidth={9999} allowEmpty={false} ariaLabel="Tipo" options={TIPOS_TORRE.map(t => ({ value: t, label: t }))} />
            </div>
            <div className={styles.filaCampos}>
              <div className={`form-group ${styles.campoFlex}`}>
                <label className="form-label">Número *</label>
                <input type="number" min="1" step="1" className="form-control" value={nombreParts.numero}
                  placeholder="Ej: 1"
                  onChange={e => setNombrePart('numero', e.target.value)} required />
              </div>
              <div className={`form-group ${styles.campoFlex}`}>
                <label className="form-label">Letra (opcional)</label>
                <SearchableSelect value={nombreParts.letra} onChange={(v) => setNombrePart('letra', v)} placeholder="— Ninguna —" minWidth={0} maxWidth={9999} ariaLabel="Letra" options={LETRAS.map(l => ({ value: l, label: l }))} />
              </div>
            </div>
            {form.numero_torre && (
              <div className={styles.vistaPrevia}>
                Se guardará como: <strong>{form.numero_torre}</strong>
              </div>
            )}
            {isSuperAdmin && (
              <div className="form-group">
                <label className="form-label">Propiedad *</label>
                {/* Una torre no cambia de conjunto: al editar, la propiedad se ve pero no se cambia. */}
                <PropertyCombobox value={form.property_id} placeholder="Seleccione..." allowEmpty={false} minWidth={0} maxWidth={9999}
                  disabled={!!editing}
                  onChange={(id) => setForm(f => ({ ...f, property_id: id }))} />
                {editing && <div className={styles.notaConjunto}>Una torre no se puede mover a otro conjunto. Si se creó en el conjunto equivocado, elimínala y créala de nuevo.</div>}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary"><i className="fas fa-save" /> Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
